import { clearStoreDataFromDatabase } from "../functions/db";

const REFRESH_COUNT_KEY = "refresh_count";
const REFRESH_TIMESTAMP_KEY = "refresh_timestamp";
const MAX_REFRESH_COUNT = 5; // Maximum allowed refreshes
const REFRESH_WINDOW_MS = 10000; // 10 seconds window

/**
 * Detects if an infinite refresh loop is occurring
 * Returns true if refresh loop is detected, false otherwise
 */
export const detectRefreshLoop = () => {
  try {
    const now = Date.now();
    const refreshCount = parseInt(sessionStorage.getItem(REFRESH_COUNT_KEY) || "0", 10);
    const lastRefreshTime = parseInt(sessionStorage.getItem(REFRESH_TIMESTAMP_KEY) || "0", 10);

    // If it's been more than REFRESH_WINDOW_MS since last refresh, reset counter
    if (now - lastRefreshTime > REFRESH_WINDOW_MS) {
      sessionStorage.setItem(REFRESH_COUNT_KEY, "1");
      sessionStorage.setItem(REFRESH_TIMESTAMP_KEY, now.toString());
      return false;
    }

    // Increment refresh count
    const newCount = refreshCount + 1;
    sessionStorage.setItem(REFRESH_COUNT_KEY, newCount.toString());
    sessionStorage.setItem(REFRESH_TIMESTAMP_KEY, now.toString());

    // If refresh count exceeds threshold, loop detected
    if (newCount >= MAX_REFRESH_COUNT) {
      console.warn(`Infinite refresh loop detected! (${newCount} refreshes in ${(now - lastRefreshTime) / 1000}s)`);
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error detecting refresh loop:", error);
    return false;
  }
};

/**
 * Clears all application data (localStorage and IndexedDB)
 * This breaks the refresh loop by removing corrupted state
 */
export const clearAllApplicationData = () => {
  try {
    console.log("Clearing all application data to break refresh loop...");

    // Clear all localStorage items
    localStorage.clear();

    // Clear Redux Persist data specifically (key: "persist:root")
    localStorage.removeItem("persist:root");

    // Clear sessionStorage refresh tracking
    sessionStorage.removeItem(REFRESH_COUNT_KEY);
    sessionStorage.removeItem(REFRESH_TIMESTAMP_KEY);

    // Clear all IndexedDB stores
    const storesToClear = [
      "userData",
      "menuList",
      "companyList",
      "branchList",
      "posSettingList",
      "holdOrders"
    ];

    storesToClear.forEach((storeName) => {
      try {
        clearStoreDataFromDatabase(storeName);
      } catch (error) {
        console.error(`Error clearing ${storeName} from IndexedDB:`, error);
      }
    });

    // Also try to delete the entire database as a fallback
    try {
      const DB_NAME = process.env.REACT_APP_DATABASE_NAME || "AppDB";
      const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
      deleteRequest.onsuccess = () => {
        console.log("IndexedDB database deleted successfully");
      };
      deleteRequest.onerror = (error) => {
        console.error("Error deleting IndexedDB database:", error);
      };
    } catch (error) {
      console.error("Error attempting to delete IndexedDB:", error);
    }

    console.log("All application data cleared successfully");
    return true;
  } catch (error) {
    console.error("Error clearing application data:", error);
    return false;
  }
};

/**
 * Resets the refresh counter (call this after successful app initialization)
 */
export const resetRefreshCounter = () => {
  try {
    sessionStorage.removeItem(REFRESH_COUNT_KEY);
    sessionStorage.removeItem(REFRESH_TIMESTAMP_KEY);
  } catch (error) {
    console.error("Error resetting refresh counter:", error);
  }
};

/**
 * Checks for refresh loop and clears data if detected
 * Call this early in app initialization (before React renders)
 */
export const checkAndHandleRefreshLoop = () => {
  if (detectRefreshLoop()) {
    clearAllApplicationData();
    // Reload the page after clearing to start fresh
    window.location.reload();
    return true;
  }
  return false;
};


